import { getCookie } from "./cookieUtils";
import { useState, useEffect, useRef } from "react";
import LoginBox from "./LoginBox";
import TrackingBox from "./TrackingBox";
import Swal from "sweetalert2";
import { toast } from "react-toastify";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Pagination } from 'antd';
import { Pencil, Trash2 } from 'lucide-react';


const CreateContactDetails = () => {
    const inputrefs = useRef({})
    const [loginToken, setLoginToken] = useState(getCookie("loginToken") || null);
    const [userName, setUserName] = useState(getCookie("userName") || "");
    const [formData, setFormData] = useState({
        destination: "",
        contactPerson: "",
        phoneNo: "",
        mobNo: "",
        faxNo: "",
        address: "",
        place: "",
        pin: "",
        isUpdate: false
    });
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [currentEditId, setCurrentEditId] = useState(null);
    const [search, setSearch] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [totalRecords, setTotalRecords] = useState(0);

    const convertToNumber = (value) => {
        return value.replace(/[^0-9]/g, '');
    }

    // Common headers for API calls
    const apiConfig = {
        AuthToken: "NASSKD28462RWKF35793FWEU757",
        LoginUserCode: userName,
        LoginToken: loginToken,
        PageIndex: String(currentPage),
        PageSize: String(pageSize),
        Search: search
    };

    // Set initial focus
    useEffect(() => {
        if (inputrefs.current["txtDestination"]) {
            inputrefs.current["txtDestination"].focus();
        }
    }, []);

    // Fetch all users on component load
    useEffect(() => {
        if (loginToken) {
            fetchUsers();
        }
    }, [loginToken, currentPage, pageSize]);

    // Show/Fetch all users
    const fetchUsers = async () => {
        setLoading(true);
        try {

            const payload = {
                ...apiConfig,
            };

            const response = await fetch(`${process.env.REACT_APP_API_URL}/api/Contact/ShowContactDetail`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(payload)
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const resData = await response.json();
            setUsers(resData.Data || []);
            setTotalRecords(Number(resData.TotalRecord) || 0);
            //setPageSize(Number(resData.PageSize) || 10);

        } catch (error) {
            console.error("Error fetching users:", error);
            toast.error("Failed to fetch users.");
        } finally {
            setLoading(false);
        }
    };


    // Save or Update user
    const handleSave = async (e) => {
        setLoading(true);
        try {
            const payload = {
                ...apiConfig,
                ...formData,
                isUpdate: editMode
            };
            const response = await fetch(`${process.env.REACT_APP_API_URL}/api/Contact/InsertUpdateContactDetail`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(payload)
            });

            const data = await response.json();

            if (data.status === "SUCCESS") {
                toast.success(data?.message);
                resetForm();
                fetchUsers();
            } else if (data.status === "ERROR") {
                toast.error(data?.message);
                const focusField = data?.focus || data?.Focus;
                if (focusField && inputrefs.current[focusField]) {
                    inputrefs.current[focusField].focus();
                }
            }

        } catch (error) {
            console.error("Error saving user:", error);
            toast.error("Failed to save user. Please check the console for details.");
        } finally {
            setLoading(false);
        }
    };

    // Edit user
    const handleEdit = async (BCodeID) => {
        setLoading(true);
        try {
            const payload = {
                ...apiConfig,
                BCodeID: BCodeID,
            };

            const response = await fetch(`${process.env.REACT_APP_API_URL}/api/Contact/EditContactDetail`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(payload)
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const userData = await response.json();
            if (userData.status === "SUCCESS") {
                setFormData({
                    destination: userData.destination || "",
                    contactPerson: userData.contactPerson || "",
                    phoneNo: userData.phoneNo || "",
                    mobNo: userData.mobNo || "",
                    faxNo: userData.faxNo || "",
                    address: userData.address || "",
                    place: userData.place || "",
                    pin: userData.pin || ""
                });
            } else {
                setFormData({
                    destination: "",
                    contactPerson: "",
                    phoneNo: "",
                    mobNo: "",
                    faxNo: "",
                    address: "",
                    place: "",
                    pin: ""
                });
            }
            setEditMode(true);
            //setCurrentEditId(userData.UserID);
            document.getElementById('userForm')?.scrollIntoView({ behavior: 'smooth' });
        } catch (error) {
            toast.error("Failed to fetch user details for editing.");
        } finally {
            setLoading(false);
        }
    };

    // Delete user
    const handleDelete = async (BCodeID) => {
        const result = await Swal.fire({
            title: "Are you sure?",
            text: "You won't be able to revert this!",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#d33",
            cancelButtonColor: "#6c757d",
            confirmButtonText: "Yes, delete it!"
        });

        if (!result.isConfirmed) return;

        setLoading(true);

        try {
            const payload = {
                ...apiConfig,
                BCodeID: BCodeID
            };

            const response = await fetch(`${process.env.REACT_APP_API_URL}/api/Contact/DeleteContactDetail`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(payload)
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            await response.json();
            await Swal.fire({
                title: "Deleted!",
                text: "User has been deleted successfully.",
                icon: "success",
                timer: 1500,
                showConfirmButton: false
            });

            fetchUsers();

        } catch (error) {
            Swal.fire({
                title: "Error!",
                text: "Failed to delete user.",
                icon: "error"
            });
        } finally {
            setLoading(false);
        }
    };

    const handleLoginSuccess = (loginData) => {
        setLoginToken(loginData.loginToken);
        setUserName(loginData.userName);
        fetchUsers();
    };

    // Reset form
    const resetForm = () => {
        setFormData({
            destination: "",
            contactPerson: "",
            phoneNo: "",
            mobNo: "",
            faxNo: "",
            address: "",
            place: "",
            pin: "",
            isUpdate: false
        });
        setEditMode(false);
        setCurrentEditId(null);
        setUsers([]);
        setTotalRecords(0);
        setCurrentPage(1);
        setSearch("");
        setTimeout(() => {
            if (inputrefs.current["txtDestination"]) inputrefs.current["txtDestination"].focus();
        }, 0);
    };

    // Handle input changes
    //const handleInputChange = (e) => {
    //    const { name, value } = e.target;
    //    setFormData(prev => ({
    //        ...prev,
    //        [name]: value
    //    }));
    //};

    return (
        <>
<section>
                <div className="container-fluid">
                    <img src="images/innerbanner.webp" className='img-fluid' alt="" />
                </div>
            </section>

            {/* MAIN SECTION */}
            <section className="main-section">
                <div className="container">
                    <div className="row g-4">

                        {/* SIDEBAR */}
                        <div className="col-lg-3">
                            <TrackingBox />
                            <LoginBox onLoginSuccess={handleLoginSuccess} />

                            {/* Contact */}
                            <div className="contact-card">
                                <div className="contact-title">Contact</div>
                                <div className="contact-item">
                                    <div className="contact-icon phone">
                                        <i className="fa fa-phone"></i>
                                    </div>
                                    <a href="tel:01141606842">+91 96672 33072</a>
                                </div>
                                <div className="contact-item">
                                    <div className="contact-icon email">
                                        <i className="fa fa-envelope"></i>
                                    </div>
                                    <a href="#">oxford_express@yahoo.co.in</a>
                                </div>
                                <div className="contact-item">
                                    <div className="contact-icon email">
                                        <i className="fa fa-envelope"></i>
                                    </div>
                                    <a href="#">info@oxfordexp.com</a>
                                </div>
                            </div>
                        </div>

                        <div className="col-lg-8">
                            {/* User Form */}
                            <div id="userForm">
                                <h4 className="mb-3">Contact Master</h4>

                                <form>
                                    <div className="row g-3">

                                        {/* Destination */}
                                        <div className="col-md-4">
                                            <div className="login-field mb-0">
                                                <label>Destination<span className="text-danger">*</span></label>
                                                <input
                                                    id="txtDestination"
                                                    ref={(el) => (inputrefs.current["txtDestination"] = el)}
                                                    type="text"
                                                    name="destination"
                                                    className="track-input"
                                                    placeholder="Enter Destination"
                                                    value={formData.destination}
                                                    onChange={(e) => setFormData({ ...formData, destination: (e.target.value) })}
                                                    required
                                                    onKeyDown={(e) => {
                                                        if (e.key === "Enter") {
                                                            e.preventDefault();
                                                            inputrefs.current["txtContactPerson"]?.focus();
                                                        }
                                                    }}
                                                />
                                            </div>
                                        </div>

                                        {/* Contact Person */}
                                        <div className="col-md-4">
                                            <div className="login-field mb-0">
                                                <label>Contact Person<span className="text-danger">*</span></label>
                                                <input
                                                    id="txtContactPerson"
                                                    ref={(el) => (inputrefs.current["txtContactPerson"] = el)}
                                                    type="text"
                                                    name="contactPerson"
                                                    className="track-input"
                                                    placeholder="Enter Contact Person"
                                                    value={formData.contactPerson}
                                                    onChange={(e) => setFormData({ ...formData, contactPerson: (e.target.value) })}
                                                    required
                                                    onKeyDown={(e) => {
                                                        if (e.key === "Enter") {
                                                            e.preventDefault();
                                                            inputrefs.current["txtPhoneNo"]?.focus();
                                                        }
                                                    }}
                                                />
                                            </div>
                                        </div>

                                        {/* Phone No */}
                                        <div className="col-md-4">
                                            <div className="login-field mb-0">
                                                <label>Phone No</label>
                                                <input
                                                    id="txtPhoneNo"
                                                    ref={(el) => (inputrefs.current["txtPhoneNo"] = el)}
                                                    type="text"
                                                    name="phoneNo"
                                                    className="track-input"
                                                    placeholder="Enter Phone No"
                                                    value={formData.phoneNo}
                                                    onChange={(e) => setFormData({ ...formData, phoneNo: convertToNumber(e.target.value) })}
                                                    onKeyDown={(e) => {
                                                        if (e.key === "Enter") {
                                                            e.preventDefault();
                                                            inputrefs.current["txtMobileNo"]?.focus();
                                                        }
                                                    }}
                                                    required
                                                    maxLength="10"
                                                />
                                            </div>
                                        </div>

                                        {/* Mobile No */}
                                        <div className="col-md-4">
                                            <div className="login-field mb-0">
                                                <label>Mobile No<span className="text-danger">*</span></label>
                                                <input
                                                    id="txtMobileNo"
                                                    ref={(el) => (inputrefs.current["txtMobileNo"] = el)}
                                                    type="text"
                                                    name="mobNo"
                                                    className="track-input"
                                                    placeholder="Enter Mobile No"
                                                    value={formData.mobNo}
                                                    onChange={(e) => setFormData({ ...formData, mobNo: convertToNumber(e.target.value) })}
                                                    required
                                                    onKeyDown={(e) => {
                                                        if (e.key === "Enter") {
                                                            e.preventDefault();
                                                            inputrefs.current["txtFaxNo"]?.focus();
                                                        }
                                                    }}
                                                    maxLength="10"
                                                />
                                            </div>
                                        </div>

                                        {/* Fax No */}
                                        <div className="col-md-4">
                                            <div className="login-field mb-0">
                                                <label>Fax No</label>
                                                <input
                                                    id="txtFaxNo"
                                                    ref={(el) => (inputrefs.current["txtFaxNo"] = el)}
                                                    type="text"
                                                    name="faxNo"
                                                    className="track-input"
                                                    placeholder="Enter Fax No"
                                                    value={formData.faxNo}
                                                    onChange={(e) => setFormData({ ...formData, faxNo: (e.target.value) })}
                                                    onKeyDown={(e) => {
                                                        if (e.key === "Enter") {
                                                            e.preventDefault();
                                                            inputrefs.current["txtAddress"]?.focus();
                                                        }
                                                    }}
                                                />
                                            </div>
                                        </div>

                                        {/* Address */}
                                        <div className="col-md-4">
                                            <div className="login-field mb-0">
                                                <label>Address</label>
                                                <input
                                                    id="txtAddress"
                                                    ref={(el) => (inputrefs.current["txtAddress"] = el)}
                                                    type="text"
                                                    name="address"
                                                    className="track-input"
                                                    placeholder="Enter Address"
                                                    value={formData.address}
                                                    onChange={(e) => setFormData({ ...formData, address: (e.target.value) })}
                                                    onKeyDown={(e) => {
                                                        if (e.key === "Enter") {
                                                            e.preventDefault();
                                                            inputrefs.current["txtPlace"]?.focus();
                                                        }
                                                    }}
                                                />
                                            </div>
                                        </div>

                                        {/* Place */}
                                        <div className="col-md-4">
                                            <div className="login-field mb-0">
                                                <label>Place</label>
                                                <input
                                                    id="txtPlace"
                                                    ref={(el) => (inputrefs.current["txtPlace"] = el)}
                                                    type="text"
                                                    name="place"
                                                    className="track-input"
                                                    placeholder="Enter Place"
                                                    value={formData.place}
                                                    onChange={(e) => setFormData({ ...formData, place: (e.target.value) })}
                                                    onKeyDown={(e) => {
                                                        if (e.key === "Enter") {
                                                            e.preventDefault();
                                                            inputrefs.current["txtPinCode"]?.focus();
                                                        }
                                                    }}
                                                />
                                            </div>
                                        </div>

                                        {/* Pin */}
                                        <div className="col-md-4">
                                            <div className="login-field mb-0">
                                                <label>Pin Code</label>
                                                <input
                                                    id="txtPinCode"
                                                    ref={(el) => (inputrefs.current["txtPinCode"] = el)}
                                                    type="text"
                                                    name="pin"
                                                    className="track-input"
                                                    placeholder="Enter Pin Code"
                                                    value={formData.pin}
                                                    onChange={(e) => setFormData({ ...formData, pin: (e.target.value) })}
                                                    onKeyDown={(e) => {
                                                        if (e.key === "Enter") {
                                                            e.preventDefault();
                                                            inputrefs.current["btnSub"]?.focus();
                                                        }
                                                    }}
                                                />
                                            </div>
                                        </div>

                                    </div>

                                    {/* Buttons */}
                                    <div className="d-flex gap-3 mt-3 w-50">
                                        <button
                                            id="btnSub"
                                            type="submit"
                                            ref={(el) => (inputrefs.current["btnSub"] = el)}
                                            className="track-btn"
                                            disabled={loading}
                                            onClick={handleSave}
                                        >
                                            <i className="fa fa-save me-1"></i>
                                            {loading ? "Processing..." : editMode ? "Update" : "Save"}
                                        </button>

                                        <button
                                            type="button"
                                            className="login-btn"
                                            onClick={resetForm}
                                        >
                                            <i className="fa fa-undo me-1"></i> Reset
                                        </button>
                                    </div>
                                </form>
                            </div>

                            {/* Users Grid */}
                            <div className="mt-5">

                                {/* ?? Search */}
                                <div className="d-flex justify-content-end mb-3">
                                    {/*<div className="d-flex gap-2 w-50">*/}
                                    {/*    <input*/}
                                    {/*        type="text"*/}
                                    {/*        className="track-input w-50 m-0"*/}
                                    {/*        placeholder="Search user..."*/}
                                    {/*        value={search}*/}
                                    {/*        onChange={(e) => setSearch(e.target.value)}*/}
                                    {/*        onKeyDown={(e) => {*/}
                                    {/*            if (e.key === "Enter") fetchUsers();*/}
                                    {/*        }}*/}
                                    {/*    />*/}
                                    {/*    <button */}
                                    {/*        type="button" */}
                                    {/*        className="track-btn m-0" */}
                                    {/*        onClick={fetchUsers} */}
                                    {/*        disabled={loading}*/}
                                    {/*        style={{ height: "44px", width: "55px" }}*/}
                                    {/*    >*/}
                                    {/*        <i className="fa fa-search me-1"></i>*/}
                                    {/*    </button>*/}
                                    {/*</div>*/}

                                    <select
                                        className="form-select w-auto"
                                        value={pageSize}
                                        onChange={(e) => {
                                            setPageSize(Number(e.target.value));
                                            setCurrentPage(1);
                                        }}
                                    >
                                        <option value={10}>10</option>
                                        <option value={50}>50</option>
                                        <option value={100}>100</option>
                                    </select>
                                </div>

                                {/* Table */}
                                <div className="table-responsive shadow-sm border rounded  overflow-x-hidden" style={{ whiteSpace: "nowrap", maxHeight: "500px" }}>

                                    {loading ? (
                                        <div className="text-center p-4">
                                            <div className="spinner-border text-primary"></div>
                                        </div>
                                    ) : (
                                        <table className="table table-striped table-hover align-middle mb-0">

                                            {/* HEADER */}
                                            <thead className="table-dark text-nowrap">
                                                <tr>
                                                        {/*<th>SrNo</th>*/}
                                                        <th className="text-center">Actions</th>
                                                    <th>Destination</th>
                                                    <th>Contact Person</th>
                                                    <th>Contact No</th>
                                                    <th>Mobile No</th>
                                                    <th>Place</th>
                                                    <th>Pin</th>
                                                   
                                                </tr>
                                            </thead>

                                            {/* BODY */}
                                            <tbody>
                                                {users.length > 0 ? (
                                                    users.map((item, index) => (
                                                        <tr key={index}>

                                                            {/*<td className="fw-semibold">{item.SrNo}</td>*/}
                                                            <td className="text-center">
                                                                <div className="d-flex justify-content-center">

                                                                    <button
                                                                        className="btn btn-sm"
                                                                        onClick={() => handleEdit(item.BCodeID)}
                                                                    >
                                                                        <Pencil size={18} className="text-primary" />
                                                                    </button>

                                                                    <button
                                                                        className="btn btn-sm"
                                                                        onClick={() => handleDelete(item.BCodeID)}
                                                                    >
                                                                        <Trash2 size={18} className="text-danger" />
                                                                    </button>

                                                                </div>
                                                            </td>
                                                            <td className="text-uppercase fw-semibold">
                                                                {item.Destination}
                                                            </td>

                                                            <td style={{
                                                                maxWidth: "180px",
                                                                whiteSpace: "normal",
                                                                wordBreak: "break-word"
                                                            }}>
                                                                {item.ContactPerson || <span className="text-muted">-</span>}
                                                            </td>

                                                            <td>
                                                                {item.ContactNo || <span className="text-muted">-</span>}
                                                            </td>

                                                            <td>
                                                                {item.MobNo || <span className="text-muted">-</span>}
                                                            </td>

                                                            <td className="text-uppercase">
                                                                {item.Place || <span className="text-muted">-</span>}
                                                            </td>

                                                            <td>
                                                                {item.Pin || <span className="text-muted">-</span>}
                                                            </td>

                                                        </tr>
                                                    ))
                                                ) : (
                                                    <tr>
                                                        <td colSpan="8" className="text-center py-4 text-muted">
                                                            No records found
                                                        </td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </table>
                                    )}
                                </div>
                                {/* ?? Pagination */}
                                <div className="d-flex justify-content-between align-items-center mt-3">
                                    <div className="text-muted">
                                        Showing {users.length} of {totalRecords} records
                                    </div>

                                    <Pagination
                                        current={currentPage}
                                        total={totalRecords}
                                        pageSize={pageSize}
                                        onChange={(page) => setCurrentPage(page)}
                                        showSizeChanger={false}
                                        size="default"
                                        showQuickJumper={false}
                                    />
                                </div>

                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* STATS STRIP */}
            <section className="stats-strip">
                <div className="container">
                    <div className="row g-0 text-center">
                        <div className="col-6 col-md-3 stat-item py-2">
                            <div className="stat-number">4.5L+</div>
                            <div className="stat-label">Packets / Month</div>
                        </div>
                        <div className="col-6 col-md-3 stat-item py-2">
                            <div className="stat-number">1000+</div>
                            <div className="stat-label">Cities Covered</div>
                        </div>
                        <div className="col-6 col-md-3 stat-item py-2">
                            <div className="stat-number">35+</div>
                            <div className="stat-label">Years Experience</div>
                        </div>
                        <div className="col-6 col-md-3 stat-item py-2">
                            <div className="stat-number">10K+</div>
                            <div className="stat-label">Happy Clients</div>
                        </div>
                    </div>
                </div>
            </section>
        </>
    );
};

export default CreateContactDetails;

